<template>
  <v-dialog :persistent="isPending" min-height="340" max-width="300">
    <v-card theme="light">
      <v-card-text>
        <v-row v-if="isSubmitted">
          <v-col>
            <v-alert
              :color="isSubmitError ? 'error' : 'success'"
              :type="isSubmitError ? 'error' : 'success'"
            >
              <span v-if="isSubmitError">
                Sorry! There was an error submitting your request. Please try a
                bit later or contact us via email.
              </span>
              <span v-else>
                Message sent! Please keep an eye out for a text from
                {{ offer.sms_sender }}.
              </span>
            </v-alert>
          </v-col>
        </v-row>

        <template v-else>
          <v-row>
            <v-col cols="12">
              <p class="text-body-1">Enter your mobile phone</p>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12">
              <IntlTelInput
                :disabled="isPending"
                :options="{
                  containerClass: 'tel-input-dialog',
                  autoPlaceholder: 'polite',
                  initialCountry: 'us',
                  separateDialCode: true,
                  loadUtils
                }"
                @change-number="number = $event"
                @change-validity="isValid = $event"
              />
            </v-col>
          </v-row>

          <v-row
            v-if="
              offer.sms_agreement &&
              typeof offer.sms_agreement === 'object' &&
              offer.sms_agreement.status === 'published'
            "
            dense
          >
            <v-col cols="auto">
              <v-checkbox
                v-model="agreed"
                :disabled="isPending"
                density="compact"
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col class="text-body-2">
              <!-- eslint-disable vue/no-v-html -->
              <div
                class="text-body-2"
                v-html="agreementBody(offer.sms_agreement.description)"
              ></div>
            </v-col>
          </v-row>
        </template>
      </v-card-text>

      <v-card-actions v-if="isSubmitted">
        <v-btn
          block
          color="secondary"
          tile
          variant="flat"
          @click="$emit('close')"
          >Close</v-btn
        >
      </v-card-actions>

      <v-card-actions v-else>
        <v-btn
          :disabled="
            !isValid ||
            isPending ||
            !!(
              !agreed &&
              offer.sms_agreement &&
              typeof offer.sms_agreement === 'object' &&
              offer.sms_agreement.is_consent_required
            )
          "
          :loading="isPending"
          color="primary"
          tile
          variant="elevated"
          @click="submit"
        >
          Submit
        </v-btn>
        <v-btn :disabled="isPending" tile variant="flat" @click="$emit('close')"
          >Cancel</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import type { DirectusSchema } from '#root/types/content'
import type { LeadBody } from '#root/rpc/client/lead'
import { ref } from 'vue'
import { usePageContext } from '#root/renderer/usePageContext'
import { useOffersOrThrow } from '#root/lib/useOffers'
import { agreementBody } from '#root/lib/utils'
// @ts-expect-error IntlTelInput is not typed
import IntlTelInput from 'intl-tel-input/vue'
import 'intl-tel-input/styles'

const props = defineProps<{
  formData: {
    email: string
    name: string
  }
  offer: DirectusSchema['offer']
}>()

defineEmits(['close'])

const { canonicalPaths } = usePageContext()
const { isPending, submitLead } = useOffersOrThrow()

const agreed = ref(false)
const isSubmitted = ref(false)
const isSubmitError = ref(false)
const isValid = ref(false)
const number = ref(null)

function loadUtils() {
  // @ts-expect-error IntlTelInput is not typed
  return import('intl-tel-input/utils')
}

async function submit() {
  if (!number.value) return

  const formData = props.formData
  const body: LeadBody = {}

  if (formData.email && formData.name) {
    body.data = {
      email: formData.email,
      first_name: formData.name,
      phone: number.value
    }

    if (props.offer.promo_code) body.data.promo_code = props.offer.promo_code

    if (props.offer.hubspot_form_id && props.offer.hubspot_portal_id) {
      body.hubspot = {
        context: {
          pageUri: canonicalPaths.absolute,
          pageName: canonicalPaths.relative
        },
        form_id: props.offer.hubspot_form_id,
        portal_id: props.offer.hubspot_portal_id
      }
    }
  }

  if (props.offer.sms_content && props.offer.sms_sender) {
    body.sms = {
      content: props.offer.sms_content,
      recipient: number.value,
      sender: props.offer.sms_sender
    }
  }

  try {
    const resp = await submitLead(body)
    if (!resp.ok) isSubmitError.value = true
  } catch (err) {
    console.error({ err }, 'Submit error')
    isSubmitError.value = true
  } finally {
    isSubmitted.value = true
  }
}
</script>

<style>
.tel-input-dialog .iti__tel-input::placeholder {
  color: #cccccc !important;
}
</style>
